import { getMomentTz } from "@dexteel/mesf-core";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import { RowClassParams, RowClickedEvent, RowStyle } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	EndFilter,
	StartFilter,
} from "../../delays/shared/components/StartEndFilter";
import { useHeatListColumns } from "../hooks/useHeatListColumns";
import { useHeatListData, useHeatResults } from "../hooks/useHeatListData";
import { useToday } from "../hooks/useToday";
import { Campaign } from "../models/Campaign";
import { HeatResultsTable } from "./heat-results/HeatResultsTable";
import { HEAT_STATUS_COLORS, HeatStatusLegend } from "./HeatStatusLegend";

// Mill-schedule row coloring by test status (see HeatStatusLegend).
const getRowStyle = (
	params: RowClassParams<Campaign>,
): RowStyle | undefined => {
	const code = params.data?.SampleStatusCode;
	if (!code) return undefined;
	const color = HEAT_STATUS_COLORS[code];
	return color ? { backgroundColor: color } : undefined;
};

const moment = getMomentTz();

type Props = {
	onError: (error: Error) => void;
};

export const HeatListCard = ({ onError }: Props) => {
	const [startDate, setStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [endDate, setEndDate] = useState<Moment>(moment().endOf("day"));
	const [selectedHeat, setSelectedHeat] = useState<Campaign | null>(null);

	// Slide the default range at day change (server TZ) unless the user
	// touched the pickers — a wall display left open must keep showing
	// today's heats.
	const todayKey = useToday();
	const userTouchedRef = useRef(false);
	const appliedDayRef = useRef(todayKey);
	useEffect(() => {
		if (appliedDayRef.current === todayKey) return;
		appliedDayRef.current = todayKey;
		if (userTouchedRef.current) return;
		setStartDate(moment().subtract(7, "days").startOf("day"));
		setEndDate(moment().endOf("day"));
	}, [todayKey]);

	const { rows, isLoading } = useHeatListData({
		start: startDate.toDate(),
		end: endDate.toDate(),
		onError,
	});

	const { results, dispositions } = useHeatResults({
		heatId: selectedHeat?.HeatId ?? null,
		onError,
	});

	const { columnDefs } = useHeatListColumns();
	const { defaultColDef } = useGridDefinitions({});

	const onRowClicked = (event: RowClickedEvent) => {
		setSelectedHeat(event.data as Campaign);
	};

	return (
		<Paper elevation={1} sx={{ p: 1, height: "100%" }}>
			<Box
				sx={{
					height: "100%",
					display: "grid",
					gridTemplateRows: "auto auto minmax(0, 1fr) 240px",
					gap: 1,
				}}
			>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					Heat List
				</Typography>

				<Grid2 container spacing={1}>
					<Grid2>
						<StartFilter
							value={startDate}
							onChange={(date) => {
								if (date) {
									userTouchedRef.current = true;
									setStartDate(date);
								}
							}}
						/>
					</Grid2>
					<Grid2>
						<EndFilter
							value={endDate}
							onChange={(date) => {
								if (date) {
									userTouchedRef.current = true;
									setEndDate(date);
								}
							}}
						/>
					</Grid2>
				</Grid2>

				<Box
					sx={{
						minHeight: 0,
						minWidth: 0,
						width: "100%",
						border: "1px solid #ccc",
						borderRadius: "4px",
						overflow: "hidden",
					}}
				>
					<AgGridReact<Campaign>
						loading={isLoading}
						rowData={rows}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						rowHeight={30}
						headerHeight={30}
						loadingOverlayComponent={CenteredLazyLoading}
						onRowClicked={onRowClicked}
						getRowId={(params) => `${params.data.CampaignId}`}
						getRowStyle={getRowStyle}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								checkboxes: false,
								enableClickSelection: true,
							},
							statusBar: {
								statusPanels: [
									{ statusPanel: HeatStatusLegend, align: "left" },
									{ statusPanel: "agTotalRowCountComponent", align: "right" },
								],
							},
						}}
					/>
				</Box>

				{/* Reserved area: blank until a heat is clicked, so results appear
				    without reflowing the layout. */}
				<Box sx={{ minHeight: 0, minWidth: 0, overflow: "auto" }}>
					{selectedHeat?.HeatId != null && (
						<>
							<Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
								Heat Results — {selectedHeat.HeatName}
							</Typography>
							{results && results.length > 0 ? (
								<HeatResultsTable
									results={results}
									dispositions={dispositions}
									maxHeight={260}
								/>
							) : (
								<Typography variant="caption" sx={{ color: "text.secondary" }}>
									No test results for this heat.
								</Typography>
							)}
						</>
					)}
				</Box>
			</Box>
		</Paper>
	);
};
